import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { UserRound, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageLoadingState } from '@/components/layout/PageLoadingState';
import { PageErrorState } from '@/components/layout/PageErrorState';
import { useAuth } from '@/hooks/useAuthApi';
import { useOrganization } from '@/hooks/useOrganization';
import { usePersonDetail } from '@/hooks/people/usePersonDetail';
import { useSessionDetail } from '@/hooks/sessions/useSessionDetail';
import PersonSessionsList from '@/components/people/PersonSessionsList';
import SessionDetail from '@/components/sessions/SessionDetail';

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function IntentBadge({ intent }: { intent: string | null | undefined }) {
  if (intent === 'high') {
    return <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400">High Intent</Badge>;
  }
  if (intent === 'medium') {
    return <Badge variant="outline" className="border-yellow-200 bg-yellow-50 text-yellow-700 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-400">Medium Intent</Badge>;
  }
  if (intent === 'low') {
    return <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400">Low Intent</Badge>;
  }
  return null;
}

function QualBadge({ status }: { status: string | null | undefined }) {
  if (status === 'qualified') {
    return <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400">Qualified</Badge>;
  }
  if (status === 'unqualified') {
    return <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400">Unqualified</Badge>;
  }
  return null;
}

const PersonDetailPage: React.FC = () => {
  const { email: rawEmail } = useParams<{ email: string }>();
  const email = rawEmail ? decodeURIComponent(rawEmail) : undefined;

  const { user, loading: authLoading } = useAuth();
  const { currentOrganization, isLoading: orgLoading } = useOrganization();

  const {
    data: personData,
    isLoading: personLoading,
    error: personError,
  } = usePersonDetail(currentOrganization?.id, email);

  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);

  // Auto-select first session
  useEffect(() => {
    if (personData?.sessions && personData.sessions.length > 0 && !selectedSessionId) {
      setSelectedSessionId(personData.sessions[0].id);
    }
  }, [personData?.sessions, selectedSessionId]);

  // Fetch full analytics for selected session (reuses existing endpoint)
  const {
    data: sessionAnalytics,
    isLoading: analyticsLoading,
  } = useSessionDetail(selectedSessionId);

  if (authLoading || orgLoading) {
    return <PageLoadingState />;
  }

  if (!user) return <div className="p-4 text-center">Please log in.</div>;
  if (!currentOrganization?.id) return <div className="p-4 text-center">Select an organization.</div>;
  if (!email) return <div className="p-4 text-center">No email specified.</div>;

  const person = personData?.person;
  const sessions = personData?.sessions || [];
  const qualStatus = person?.updated_qualification_status || person?.qualification_status;
  const displayLabel = person?.name || email;

  return (
    <>
      <Helmet>
        <title>{displayLabel} | People | Hobbes</title>
      </Helmet>
      <div className="flex h-full min-h-0 flex-col">
        <PageHeader
          breadcrumbs={[
            { label: 'People', href: '/people', icon: UserRound },
            { label: displayLabel },
          ]}
          titleTooltip="Visitor activity, qualification, and session history."
          metadata={
            person && !personLoading ? (
              <div className="ml-0.5 mt-1 flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span>{person.email}</span>
                  {person.business && <span>Company · {person.business}</span>}
                  {person.phone && <span>Phone · {person.phone}</span>}
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-xs text-muted-foreground">
                    Last seen {formatDate(person.last_seen_at)}
                  </span>
                  <IntentBadge intent={person.buying_intent} />
                  <QualBadge status={qualStatus} />
                </div>
              </div>
            ) : undefined
          }
        />

        {/* Loading */}
        {personLoading && (
          <PageLoadingState className="flex-1" />
        )}

        {/* Error */}
        {personError && (
          <PageErrorState
            className="flex-1"
            message={personError instanceof Error ? personError.message : 'Failed to load person'}
          />
        )}

        {/* Main content */}
        {!personLoading && !personError && person && (
          <div className="flex-1 overflow-hidden px-4 sm:px-6 md:px-8 pb-4">
            <div className="rounded-xl border card-border card-bg overflow-hidden h-full flex min-w-0 flex-col xl:flex-row">
              {/* Left: Sessions list */}
              <div className="h-[42%] min-h-[260px] shrink-0 border-b card-border flex flex-col card-bg xl:h-auto xl:w-[350px] xl:border-b-0 xl:border-r">
                <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">Sessions</span>
                  <span className="text-xs text-muted-foreground">{sessions.length}</span>
                </div>
                <div className="flex-1 overflow-y-auto">
                  <PersonSessionsList
                    sessions={sessions}
                    selectedSessionId={selectedSessionId}
                    onSelectSession={setSelectedSessionId}
                  />
                </div>
              </div>

              {/* Center + Right: Session detail */}
              <SessionDetail
                analytics={sessionAnalytics}
                organizationId={currentOrganization?.id}
                isLoadingDetail={analyticsLoading}
                mediaLayout="natural"
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PersonDetailPage;
